import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createElementVNode as _createElementVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "flex items-center justify-center mt-1 w-full" }
const _hoisted_2 = { class: "mt-4 text-xs text-gray-500" }

import { computed, ref } from 'vue';
import Button from '@/components/ui/button/Button.vue';
import Input from '@/components/ui/input/Input.vue';
import SelectContent from '@/components/ui/select/SelectContent.vue';
import SelectGroup from '@/components/ui/select/SelectGroup.vue';
import SelectItem from '@/components/ui/select/SelectItem.vue';
import Select from '@/components/ui/select/Select.vue';
import SelectTrigger from '@/components/ui/select/SelectTrigger.vue';
import SelectValue from '@/components/ui/select/SelectValue.vue';
import ErrorForm from '@/components/auth/ErrorForm.vue';
import { submitPartnerCreationRequest } from '@/http/registration';
import { useStore } from 'vuex';
import { AuthenticationError } from '@/http/api';
import { CLEAR_AUTH, PARTNER_ID } from '@/store';
import { get } from '@vueuse/core';
import { useToast } from '@/components/ui/toast';
import { PartnerField, PartnerSize } from '@/models/partner';

type PARTNER_SIZE = '1-5 Funcionários' | 'Acima de 11 Funcionários' | '6-10 Funcionários';
type PARTNER_FIELD =
  | 'TI'
  | 'Finanças'
  | 'Moda'
  | 'Restaurante'
  | 'Logística'
  | 'Entrega de comida'
  | 'Agro';

export default /*#__PURE__*/_defineComponent({
  __name: 'PartnerForm',
  emits: ['nextStep', 'TOGGLE_REGISTRATION'],
  setup(__props, { emit: __emit }) {

const store = useStore();
const { toast } = useToast();

const AVAILABLE_PARTNER_SIZES: Record<PARTNER_SIZE, PartnerSize> = {
  '1-5 Funcionários': 'LARGE',
  '6-10 Funcionários': 'MEDIUM',
  'Acima de 11 Funcionários': 'SMALL',
};
const AVAILABLE_PARTNER_FIELDS: Record<PARTNER_FIELD, PartnerField> = {
  TI: 'IT',
  Finanças: 'FINANCES',
  Restaurante: 'RESTAURANTS',
  Moda: 'BEAUTY',
  Agro: 'AGRO',
  'Entrega de comida': 'FOOD_SUPPLY',
  Logística: 'LOGISTICS',
};

const hasEverSubmitted = ref(false);

const partnerName = ref<string | undefined>(undefined);
const validatePartnerName = (value?: string): boolean => {
  if (value === undefined) return false;
  return /.*/.test(value ?? '');
};
const showPartnerNameError = computed(() => validatePartnerName(partnerName.value) || (!hasEverSubmitted.value && partnerName.value === undefined));

const cnpj = ref<string | undefined>(undefined);
function validateCnpj(cnpj: string | undefined): boolean {
  if (!cnpj) return false;

  cnpj = cnpj.trim();
  if (/[^0-9.\-/]/.test(cnpj)) return false;

  cnpj = cnpj.replace(/[^\d]+/g, '');

  if (cnpj.length !== 14) return false;

  if (/^(\d)\1{13}$/.test(cnpj)) return false;

  const firstDigits = cnpj.substring(0, 12);
  let sum = 0;
  let pos = firstDigits.length - 7;

  for (let i = 0; i < 12; i++) {
    sum += Number(firstDigits[i]) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== Number(cnpj[12])) return false;

  const firstTwelveDigits = cnpj.substring(0, 13);
  sum = 0;
  pos = firstTwelveDigits.length - 7;

  for (let i = 0; i < 13; i++) {
    sum += Number(firstTwelveDigits[i]) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return result === Number(cnpj[13]);
}
const showCnpjError = computed(() => validateCnpj(cnpj.value) || (!hasEverSubmitted.value && cnpj.value === undefined));

const partnerField = ref<PartnerField | undefined>(undefined);
const validatePartnerField = (value?: PartnerField): boolean => value !== undefined;
const showPartnerFieldError = computed(() => validatePartnerField(partnerField.value) || (!hasEverSubmitted.value && partnerField.value === undefined));

const partnerSize = ref<PartnerSize | undefined>(undefined);
const validatePartnerSize = (value?: PartnerSize): boolean => value !== undefined;
const showPartnerSizeError = computed(() => validatePartnerSize(partnerSize.value) || (!hasEverSubmitted.value && partnerSize.value === undefined));

const emit = __emit;
const onSubmit = async () => {
  validatePartnerName(partnerName.value);
  validateCnpj(cnpj.value);
  validatePartnerField(partnerField.value);
  validatePartnerSize(partnerSize.value);

  hasEverSubmitted.value = true;

  if (!(get(showPartnerNameError) && get(showCnpjError) && get(showPartnerFieldError) && get(showPartnerSizeError)) || partnerField.value === undefined || partnerSize.value === undefined || cnpj.value === undefined || partnerName.value === undefined) {
    return;
  }

  try {
    const partnerCreationResponse = await submitPartnerCreationRequest(
      {
        partnerName: partnerName.value,
        cnpj: cnpj.value,
        partnerField: partnerField.value,
        partnerSize: partnerSize.value,
      },
      store.state.credentials
    );
    store.commit(PARTNER_ID, partnerCreationResponse.id);
  } catch (err) {
    if (err instanceof AuthenticationError) {
      store.commit(CLEAR_AUTH);
      emit('TOGGLE_REGISTRATION');

      return;
    }
    toast({
      variant: 'destructive',
      title: 'Não foi possível criar sua empresa',
      description: 'Tente novamente com os dados corretos',
    });
    return;
  }
  emit('nextStep');
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(Input, {
      type: "text",
      placeholder: "Nome da Empresa",
      modelValue: partnerName.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((partnerName).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(ErrorForm, {
      class: _normalizeClass({ invisible: showPartnerNameError.value })
    }, {
      default: _withCtx(() => [
        _createTextVNode(" Escreva um nome de empresa válido ")
      ]),
      _: 1
    }, 8, ["class"]),
    _createVNode(Input, {
      type: "text",
      placeholder: "CNPJ",
      modelValue: cnpj.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((cnpj).value = $event))
    }, null, 8, ["modelValue"]),
    _createVNode(ErrorForm, {
      class: _normalizeClass({ invisible: showCnpjError.value })
    }, {
      default: _withCtx(() => [
        _createTextVNode(" Por favor, insira um CNPJ válido. ")
      ]),
      _: 1
    }, 8, ["class"]),
    _createVNode(Select, {
      modelValue: partnerField.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((partnerField).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(SelectTrigger, null, {
          default: _withCtx(() => [
            _createVNode(SelectValue, {
              class: _normalizeClass({
            'text-gray-500': partnerField.value === undefined,
          }),
              placeholder: "Área de Atuação"
            }, null, 8, ["class"])
          ]),
          _: 1
        }),
        _createVNode(SelectContent, null, {
          default: _withCtx(() => [
            _createVNode(SelectGroup, null, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.keys(AVAILABLE_PARTNER_FIELDS), (availablePartnerField) => {
                  return (_openBlock(), _createBlock(SelectItem, {
                    key: availablePartnerField,
                    value: AVAILABLE_PARTNER_FIELDS[availablePartnerField as PARTNER_FIELD]
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(availablePartnerField), 1)
                    ]),
                    _: 2
                  }, 1032, ["value"]))
                }), 128))
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(ErrorForm, {
      class: _normalizeClass({ invisible: showPartnerFieldError.value })
    }, {
      default: _withCtx(() => [
        _createTextVNode(" Selecione uma área de atuação. ")
      ]),
      _: 1
    }, 8, ["class"]),
    _createVNode(Select, {
      modelValue: partnerSize.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((partnerSize).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(SelectTrigger, null, {
          default: _withCtx(() => [
            _createVNode(SelectValue, {
              class: _normalizeClass({ 'text-gray-500': partnerSize.value === undefined }),
              placeholder: "Porte da Empresa"
            }, null, 8, ["class"])
          ]),
          _: 1
        }),
        _createVNode(SelectContent, null, {
          default: _withCtx(() => [
            _createVNode(SelectGroup, null, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.keys(AVAILABLE_PARTNER_SIZES), (availablePartnerSize) => {
                  return (_openBlock(), _createBlock(SelectItem, {
                    key: availablePartnerSize,
                    value: AVAILABLE_PARTNER_SIZES[availablePartnerSize as PARTNER_SIZE]
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(availablePartnerSize), 1)
                    ]),
                    _: 2
                  }, 1032, ["value"]))
                }), 128))
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(ErrorForm, {
      class: _normalizeClass({ invisible: showPartnerSizeError.value })
    }, {
      default: _withCtx(() => [
        _createTextVNode(" Selecione um porte da empresa. ")
      ]),
      _: 1
    }, 8, ["class"]),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(Button, {
        class: "w-full text-white rounded-full",
        variant: "default",
        type: "submit",
        size: "sm",
        onClick: onSubmit
      }, {
        default: _withCtx(() => [
          _createTextVNode(" Cadastrar ")
        ]),
        _: 1
      })
    ]),
    _createElementVNode("p", _hoisted_2, [
      _createTextVNode(" Já tinha uma conta?"),
      _createElementVNode("a", {
        class: "text-blue-500 cursor-pointer",
        onClick: _cache[4] || (_cache[4] = () => {
          _unref(store).commit(_unref(CLEAR_AUTH));

          _ctx.$emit('TOGGLE_REGISTRATION');
        })
      }, " Logar")
    ])
  ]))
}
}

})